import * as ko from 'knockout';
import * as localisationHelper from 'localisationHelper';
import * as Signals from 'signals';

// Services
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";
import * as BankDetailsService from 'Content/ts/Shared/Services/BankDetails/bankDetailsService';

// Helpers
import { StringFunctions } from "Content/ts/Shared/Functions/stringFunctions";

// Models
import { IBankDetailsRequestModel } from 'Content/ts/Models/Join/Interfaces/IBankDetailsRequestModel';
import { MandateDetails } from 'Content/ts/Models/Join/MandateDetails';
import { FeeProcessType } from 'Content/ts/Models/Shared/Enums/feeProcessType';
import { AlertStructure } from "Content/ts/Models/Shared/BootstrapAlerts/alertStructure";
import { DirectDebitConfirmationConfiguration } from 'Content/ts/Models/Shared/DirectDebitDetails/DirectDebitConfirmationConfiguration';

// Components
import 'Content/ts/Shared/BootstrapAlerts/AlertDanger/alert-danger';
import 'Content/ts/Shared/CollectionDate/collection-date';
import 'Content/ts/Shared/DirectDebitDetails/dd-bacs';
import 'Content/ts/Shared/DirectDebitDetails/dd-canda';
import 'Content/ts/Shared/DirectDebitDetails/dd-pnz';
import 'Content/ts/Shared/DirectDebitDetails/dd-sepa';
import 'Content/ts/Shared/Inputs/Checkbox/input-checkbox-observable';

interface DirectDebitDetailsParams {
    MandateDetails: MandateDetails | KnockoutObservable<MandateDetails>;
    FacilityId: KnockoutObservable<number>;
    BillingCycleId: KnockoutObservable<number>;
    ValidationFields: KnockoutObservable<any>[];
    PriceLoading: KnockoutObservable<boolean>;
    IsDetailEntryDisabled: KnockoutObservable<boolean>;
    ShowCollectionDate: KnockoutObservable<boolean>;
    DefaultCollectionDay: KnockoutComputed<number>;
    CollectionDateChanged: Signals.Signal;
    DirectDebitInitialise: Signals.Signal;
    DirectDebitConfirmationConfiguration: DirectDebitConfirmationConfiguration;
    AutoInitialise: boolean;
    AccountHolderConfirmation: KnockoutObservable<boolean>;
}

export const Deps = {
    BankDetailsService: BankDetailsService
}

export class DirectDebitDetails {
    customMessages: {
        AccountName: string;
        AccountHolderValidation: string;
        AccountHolderConfirmation: string;
        InvalidAccountNumber: string;
        InvalidSortCode: string;
        InvalidBIC: string;
        InvalidIBAN: string;
        NoBIC: string;
        InvalidBIClength: string;
        InvalidBICInstitutioncharacter: string;
        InvalidBICCountrycharacter: string;
        UnknownBICCountrycode: string;
        NotPermittedBICLocationcharacter: string;
        InvalidBICLocationcharacter: string;
        TestTrainingBIC: string;
        NonSWIFTBIC: string;
        NotPermittedBICBranchCode: string;
        InvalidBICBranchcharacter: string;
        IBANAndBICDoNotMatch: string;
    };

    accountNumber: KnockoutObservable<string>;
    sortCode: KnockoutObservable<string>;
    iban: KnockoutObservable<string>;
    bic: KnockoutObservable<string>;
    genericAccountNumber: KnockoutObservable<string>;
    genericSortCode: KnockoutObservable<string>;
    canadaAccountNumber: KnockoutObservable<string>;
    canadaSortCode: KnockoutObservable<string>;
    accountName: KnockoutObservable<string>;
    feeProcessType: KnockoutObservable<FeeProcessType>;
    accountHolderConfirmation: KnockoutObservable<boolean>;

    bankDetailsValid: KnockoutObservable<boolean>;
    bankDetailsHasBeenValidated: KnockoutObservable<boolean>;
    isValidating: KnockoutObservable<boolean>;
    isRequired: KnockoutObservable<boolean>;
    validationFields: KnockoutObservable<any>[];
    priceLoading: KnockoutObservable<boolean>;
    isDetailEntryDisabled: KnockoutObservable<boolean>;

    facilityId: KnockoutObservable<number>;
    billingCycleId: KnockoutObservable<number>;

    bankDetailsUpdated: Signals.Signal;
    collectionDateChanged: Signals.Signal;
    directDebitInitialise: Signals.Signal;

    directDebitConfirmationConfiguration: DirectDebitConfirmationConfiguration;

    showBacs: KnockoutComputed<boolean>;
    showSEPA: KnockoutComputed<boolean>;
    showPNZ: KnockoutComputed<boolean>;
    showCanada: KnockoutComputed<boolean>;
    showBankDetailsInvalidErrorMessage: KnockoutComputed<boolean>;
    defaultCollectionDay: KnockoutComputed<number>;
    showCollectionDate: KnockoutObservable<boolean>;
    autoInitialise: boolean;
    firstLoad: boolean;
    bankValidationErrorMessage: KnockoutObservableArray<AlertStructure>;

    constructor(params: DirectDebitDetailsParams) {
        this.validationFields = params.ValidationFields || [];

        this.facilityId = params.FacilityId;
        this.billingCycleId = params.BillingCycleId;
        this.autoInitialise = params.AutoInitialise ?? true;
        this.directDebitInitialise = params.DirectDebitInitialise || new Signals.Signal();

        this.accountNumber = ko.unwrap(params.MandateDetails).AccountNumber;
        this.sortCode = ko.unwrap(params.MandateDetails).SortCode;
        this.iban = ko.unwrap(params.MandateDetails).Iban;
        this.bic = ko.unwrap(params.MandateDetails).Bic;
        this.genericAccountNumber = ko.unwrap(params.MandateDetails).GenericAccountNumber;
        this.genericSortCode = ko.unwrap(params.MandateDetails).GenericSortCode;
        this.canadaAccountNumber = ko.unwrap(params.MandateDetails).CanadaAccountNumber;
        this.canadaSortCode = ko.unwrap(params.MandateDetails).CanadaSortCode;

        this.showCollectionDate = params.ShowCollectionDate ?? ko.observable(false);
        this.directDebitConfirmationConfiguration = params.DirectDebitConfirmationConfiguration;

        this.accountName = ko.unwrap(params.MandateDetails).AccountName;
        this.feeProcessType = ko.unwrap(params.MandateDetails).FeeProcessType;

        this.collectionDateChanged = params.CollectionDateChanged;
        this.priceLoading = params.PriceLoading || ko.observable(false);
        this.isDetailEntryDisabled = params.IsDetailEntryDisabled || ko.observable(false);

        this.defaultCollectionDay = params.DefaultCollectionDay;

        this.bankDetailsValid = ko.observable(true);
        this.bankDetailsHasBeenValidated = ko.observable(false);
        this.accountHolderConfirmation = params.AccountHolderConfirmation || ko.observable(false);
        this.isValidating = ko.observable(false);
        this.isRequired = ko.observable(false);

        this.bankDetailsUpdated = new Signals.Signal();
        this.bankDetailsUpdated.add(this.validateBankDetails.bind(this));

        this.firstLoad = true;
        this.bankValidationErrorMessage = ko.observableArray([]);
        this.retrieveCustomMessages();

        if (this.facilityId()) {
            // if facilityId is already set, the facilityId subscription below to
            // get the dd config won't be fired, so load dd configuration here
            this.getDDConfiguration();
        }

        // load configuration
        if (this.autoInitialise) {
            this.initialise();
        }
        else {
            this.directDebitInitialise.add((enabled: boolean) => {
                if (enabled) {
                    this.initialise();
                }
                else if (!this.firstLoad) {
                    this.dispose();
                }
            });
        }

        this.setupSubscriptions();
        this.setupComputeds();
    }

    private getDDConfiguration(): void {
        // The endpoint will find the logged in user facility id if null
        Deps.BankDetailsService.getDirectDebitConfigurationForFacility(this.facilityId())
            .then((feeProcessType: FeeProcessType) => {
                this.feeProcessType(feeProcessType);
            });
    }

    private setupComputeds(): void {
        this.showBacs = ko.pureComputed(() => {
            return this.feeProcessType() === FeeProcessType.DirectDebit;
        });

        this.showSEPA = ko.pureComputed(() => {
            return this.feeProcessType() === FeeProcessType.SEPA;
        });

        this.showPNZ = ko.pureComputed(() => {
            return this.feeProcessType() === FeeProcessType.PNZ;
        });

        this.showCanada = ko.pureComputed(() => {
            return this.feeProcessType() === FeeProcessType.CPA;
        });

        this.showBankDetailsInvalidErrorMessage = ko.pureComputed(() => {
            return !this.bankDetailsValid() && this.bankDetailsHasBeenValidated();
        });
    }

    private retrieveCustomMessages(): void {
        this.customMessages = {
            AccountName: StringFunctions.ToSentenceCase(localisationHelper.getString("GenericResources", "AccountHolderName")),
            AccountHolderValidation: localisationHelper.getString("GenericResources", "Validation_AccountHolder"),
            AccountHolderConfirmation: localisationHelper.getString("GenericResources", "AccountHolderConfirmation"),
            InvalidAccountNumber: localisationHelper.getString("GenericResources", "InvalidAccountNumber"),
            InvalidSortCode: localisationHelper.getString("GenericResources", "InvalidSortCode"),
            InvalidBIC: localisationHelper.getString("GenericResources", "InvalidBIC"),
            InvalidIBAN: localisationHelper.getString("GenericResources", "InvalidIBAN"),
            NoBIC: localisationHelper.getString("GenericResources", "NoBIC"),
            InvalidBIClength: localisationHelper.getString("GenericResources", "InvalidBIClength"),
            InvalidBICInstitutioncharacter: localisationHelper.getString("GenericResources", "InvalidBICInstitutioncharacter"),
            InvalidBICCountrycharacter: localisationHelper.getString("GenericResources", "InvalidBICCountrycharacter"),
            UnknownBICCountrycode: localisationHelper.getString("GenericResources", "UnknownBICCountrycode"),
            NotPermittedBICLocationcharacter: localisationHelper.getString("GenericResources", "NotPermittedBICLocationcharacter"),
            InvalidBICLocationcharacter: localisationHelper.getString("GenericResources", "InvalidBICLocationcharacter"),
            TestTrainingBIC: localisationHelper.getString("GenericResources", "TestTrainingBIC"),
            NonSWIFTBIC: localisationHelper.getString("GenericResources", "NonSWIFTBIC"),
            NotPermittedBICBranchCode: localisationHelper.getString("GenericResources", "NotPermittedBICBranchCode"),
            InvalidBICBranchcharacter: localisationHelper.getString("GenericResources", "InvalidBICBranchcharacter"),
            IBANAndBICDoNotMatch: localisationHelper.getString("GenericResources", "IBANAndBICDoNotMatch"),
        };
    }

    private setupValidationFields(): void {
        // Shared by all dd components
        ValidationService.AddValidationRule(this.accountName, this.validationFields, {
            maxLength: {
                params: 100,
                message: this.customMessages.AccountHolderValidation
            }
        });

        ValidationService.AddValidationRule(this.isValidating, this.validationFields, {
            validation: {
                validator: function (isValidating: boolean): boolean {
                    return !isValidating;
                }
            }
        });

        ValidationService.AddValidationRule(this.bankDetailsValid, this.validationFields, {
            validation: {
                validator: (val: boolean) => val,
                message: this.bankValidationErrorMessage()[0]
            }
        });
    }

    private removeValidationFields(): void {
        ValidationService.RemoveValidationRule(this.accountName, this.validationFields);
        ValidationService.RemoveValidationRule(this.isValidating, this.validationFields);
        ValidationService.RemoveValidationRule(this.bankDetailsValid, this.validationFields);
    }

    private setupSubscriptions(): void {
        // load configuration
        this.facilityId.subscribe(() => {
            this.getDDConfiguration();
        });
    }

    // shared
    private validateBankDetails(): void {
        const bankDetails = this.getBankDetails();
        const accountNumber = bankDetails[0];
        const sortCode = bankDetails[1];
        //InvalidBankMessage
        const InvalidSortCode = "InvalidSortCode";
        const InvalidAccountNumber = "InvalidAccountNumber";
        const InvalidIBAN = "InvalidIBAN";
        const InvalidBIC = "InvalidBIC";
        const NoBIC = "NoBIC";
        const InvalidBIClength = "InvalidBIClength";
        const InvalidBICInstitutioncharacter = "InvalidBICInstitutioncharacter";
        const InvalidBICCountrycharacter = "InvalidBICCountrycharacter";
        const UnknownBICCountrycode = "UnknownBICCountrycode";
        const NotPermittedBICLocationcharacter = "NotPermittedBICLocationcharacter";
        const InvalidBICLocationcharacter = "InvalidBICLocationcharacter";
        const TestTrainingBIC = "Test&TrainingBIC";
        const NonSWIFTBIC = "NonSWIFTBIC";
        const NotPermittedBICBranchCode = "NotPermittedBICBranchCode";
        const InvalidBICBranchcharacter = "InvalidBICBranchcharacter";
        const IBANAndBICDoNotMatch = "IBANAndBICDoNotMatch";

        if (accountNumber().length && sortCode().length
            && accountNumber.isValid() && sortCode.isValid()
            && this.accountName.isValid()) {
            this.bankValidationErrorMessage([]);
            this.bankDetailsHasBeenValidated(false);
            this.isValidating(true);
            const requestModel = {
                AccountNumber: accountNumber(),
                SortCode: sortCode(),
                AccountHolder: this.accountName(),
                FeeProcessType: this.feeProcessType()
            } as IBankDetailsRequestModel;

            Deps.BankDetailsService.validateBankDetails(requestModel)
                .then((result: any) => {
                    this.bankDetailsValid(result.IsValid);
                    if (!result.IsValid) {
                        result.MessageKeys.forEach((element: string, index: number) => {
                            switch (element) {
                                case InvalidAccountNumber:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidAccountNumber });
                                    break;
                                case InvalidSortCode:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidSortCode });
                                    break;
                                case InvalidIBAN:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidIBAN });
                                    break;
                                case InvalidBIC:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidBIC });
                                    break;
                                case NoBIC:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.NoBIC });
                                    break;
                                case InvalidBIClength:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidBIClength });
                                    break;
                                case InvalidBICInstitutioncharacter:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidBICInstitutioncharacter });
                                    break;
                                case InvalidBICCountrycharacter:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidBICCountrycharacter });
                                    break;
                                case UnknownBICCountrycode:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.UnknownBICCountrycode });
                                    break;
                                case NotPermittedBICLocationcharacter:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.NotPermittedBICLocationcharacter });
                                    break;
                                case InvalidBICLocationcharacter:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidBICLocationcharacter });
                                    break;
                                case TestTrainingBIC:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.TestTrainingBIC });
                                    break;
                                case NonSWIFTBIC:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.NonSWIFTBIC });
                                    break;
                                case NotPermittedBICBranchCode:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.NotPermittedBICBranchCode });
                                    break;
                                case InvalidBICBranchcharacter:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidBICBranchcharacter });
                                    break;
                                case IBANAndBICDoNotMatch:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.IBANAndBICDoNotMatch });
                                    break;
                                default:
                                    this.bankValidationErrorMessage.push({ Order: index + 1, TextValue: this.customMessages.InvalidBIC });
                                    break;
                            }
                        })
                    }
                })
                .catch(() => {
                    this.bankDetailsValid(false);
                })
                .finally(() => {
                    this.bankDetailsHasBeenValidated(true);
                    this.isValidating(false);
                });
        }
    }

    private getBankDetails(): [KnockoutObservable<string>, KnockoutObservable<string>] {
        let bankDetails: [KnockoutObservable<string>, KnockoutObservable<string>];

        switch (this.feeProcessType()) {
            case FeeProcessType.DirectDebit:
                bankDetails = [this.accountNumber, this.sortCode];
                break;
            case FeeProcessType.SEPA:
                bankDetails = [this.iban, this.bic]
                break;
            case FeeProcessType.CPA:
                bankDetails = [this.canadaAccountNumber, this.canadaSortCode]
                break;
            case FeeProcessType.PNZ:
                bankDetails = [this.genericAccountNumber, this.genericSortCode]
                break;
            case FeeProcessType.CreditCard:
                break;
            default:
                console.warn('No fee process type');
        }
        return bankDetails;
    }

    private initialise(): void {
        this.setupValidationFields();
        this.isRequired(true);
        this.firstLoad = false;
        this.bankDetailsValid(false);
    }

    public dispose(): void {
        this.removeValidationFields();
        this.isRequired(false);
        this.bankDetailsValid(true);
    }
}

const componentName = 'direct-debit-details';
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: DirectDebitDetails,
    template: { require: `text!/Themes/Default/Content/ts/Shared/DirectDebitDetails/${componentName}.html` }
});